import {
    ALL_OPTIONS_SELECTED_VALUE,
    FilterInputNameEnum,
    getFiltersChanges
} from "@vms/SelectFilters/getFiltersChanges";
import { useCurrentCurrency } from "@vms/Country/useCurrentCountryCode";
import { useCallback } from "react";
import { FilterInput } from "@vms/SelectFilters/SelectFiltersTypes";
import { SettingsItem, SettingsValue } from "@vms/AiResult/AiResultSettings";
import { useRestaurantMenu } from "@vms/RestaurantMenu";
import { mapFiltersToClient } from "@vms/SelectFilters/mapFiltersToClient";
import { getDefaultFilterData, useFilters } from "@vms/SelectFilters/SelectFiltersContext";
import {
    getVenueFilters,
    getAttributesFilters,
    TooltipOptionsList,
    getTagsFilters, getOtherFilters,
} from "@vms/SelectFilters/aiSettingsFilters.helpers";

export enum SettingsFilterSections{
    VENUE = 'venue',
    ATTRIBUTES = 'attributes',
    ITEM_TYPES = 'itemTypes',
    TAGS = 'tags',
    OTHER = 'other',
}

export const SETTINGS_COLUMNS = [
    [SettingsFilterSections.VENUE, SettingsFilterSections.ITEM_TYPES, SettingsFilterSections.TAGS],
    [SettingsFilterSections.ATTRIBUTES, SettingsFilterSections.OTHER],
]

export const getSettingsColumns = (filters: SettingsItem[]): SettingsItem[][] => [
    filters.filter(i => SETTINGS_COLUMNS[0].includes(i.id as SettingsFilterSections)),
    filters.filter(i => SETTINGS_COLUMNS[1].includes(i.id as SettingsFilterSections)),
];

const AI_SETTINGS_FILTERS_SECTIONS: Array<{
    id: SettingsFilterSections;
    title: string;
    inputs: FilterInputNameEnum[];
    defaultValues?: SettingsValue[];
}> = [
    {
        id: SettingsFilterSections.VENUE,
        title: "Venue Filters",
        defaultValues: [{
            id: 'no venue filters',
            title: 'All',
        }],
        inputs: [
            FilterInputNameEnum.Segments,
            FilterInputNameEnum.Regions,
            FilterInputNameEnum.Cuisines,
            FilterInputNameEnum.Restaurants,
        ],
    },
    {
        id: SettingsFilterSections.ITEM_TYPES,
        title: "Item Types",
        defaultValues: [{
            id: 'no item type filters',
            title: 'All',
        }],
        inputs: [
            FilterInputNameEnum.Appetizers,
            FilterInputNameEnum.Entrees,
            FilterInputNameEnum.Sides,
            FilterInputNameEnum.Desserts,
            FilterInputNameEnum.Beverages,
        ],
    },
    {
        id: SettingsFilterSections.ATTRIBUTES,
        title: "Attribute Filters",
        inputs: [
            FilterInputNameEnum.DayParts,
        ],
    },
    {
        id: SettingsFilterSections.TAGS,
        title: "AI Tags",
        inputs: [],
    },
    {
        id: SettingsFilterSections.OTHER,
        title: "Other Filters",
        inputs: [
            FilterInputNameEnum.IncludeAllWords,
            FilterInputNameEnum.IncludeAnyWords,
            FilterInputNameEnum.ExcludeWords,
        ],
    }
];

export function useGetAiSettingsFilters() {
    const currentCurrency = useCurrentCurrency();
    const {
        rawFilterData,
    } = useRestaurantMenu();
    const { tags, tagCategories } = useFilters();

    return useCallback(
        (apiFilters: FilterInput): SettingsItem[] => {
            const restaurantListId = apiFilters?.restaurantFilters?.restaurantListId ?? 0;
            const filters = mapFiltersToClient(
                restaurantListId,
                getDefaultFilterData(restaurantListId, rawFilterData),
                apiFilters,
            );
            const { changes } = getFiltersChanges(filters, currentCurrency);

            return AI_SETTINGS_FILTERS_SECTIONS
                .map(section => {
                    const result: SettingsItem = {
                        id: section.id,
                        title: section.title,
                        values: [],
                    };

                    section.inputs.forEach(input => {
                        const inputOptions = changes[input];

                        if (inputOptions) {
                            const option1 = inputOptions[0];
                            const optionsCountLabel = ` (${inputOptions.length})`;
                            let option1Label = `: ${option1}`;

                            if (option1 === ALL_OPTIONS_SELECTED_VALUE) {
                                option1Label = " (All)";
                            }

                            result.values?.push({
                                id: input,
                                title: `${input}${inputOptions.length === 1 ? option1Label : optionsCountLabel}`,
                                tooltip: inputOptions.length === 1 ? null : (
                                    <TooltipOptionsList
                                        data={inputOptions.map((o) => ({
                                            id: o,
                                            name: o,
                                        }))}
                                    />
                                ),
                            });
                        }
                    });

                    switch (section.id) {
                        case SettingsFilterSections.VENUE: {
                            const venueFilters = getVenueFilters(changes);
                            result.values?.push(...venueFilters);
                            break;
                        }
                        case SettingsFilterSections.ATTRIBUTES: {
                            const attributesFilters = getAttributesFilters(changes);
                            result.values?.push(...attributesFilters);
                            break;
                        }
                        case SettingsFilterSections.TAGS: {
                            const tagsFilters = getTagsFilters(changes, tags, tagCategories);
                            result.values?.push(...tagsFilters);
                            break;
                        }
                        case SettingsFilterSections.OTHER: {
                            const otherFilters = getOtherFilters(changes);
                            result.values?.push(...otherFilters);
                            break;
                        }
                    }

                    if (result.values?.length === 0 && section.defaultValues) {
                        result.values = section.defaultValues;
                    }

                    return result;
                })
                .filter(i => i.values && i.values.length > 0);
        },
        [currentCurrency, rawFilterData, tags, tagCategories]
    );
}